import { GameState, GameEvent, Phase, CardDef } from '../types.js';
import { cloneState, getPlayerState, getOpponentSide, getInstance, dispatch, adjustClock } from './GameState.js';
import { drawCards } from './DeckUtils.js';
import {
  getMaxHandSize,
  hasPassive,
  hasPassiveAttackReady,
  processDamageFrozen,
  processBeginningOfTurnTriggers,
  processEndOfTurnTriggers,
} from './AbilityProcessor.js';
import { sweepPassiveKOs } from './CombatResolver.js';
import { getConfig } from '../config.js';

/** Whether a side's ring is currently empty (no instanceIds in any slot). */
function ringIsEmpty(state: GameState, side: 'player' | 'opponent'): boolean {
  return getPlayerState(state, side).ring.every(s => !s.instanceId);
}

/** Voice-line state checks. Each fires at most once per match — checked
 *  against the corresponding latch on game state. Returns the event to
 *  emit, or null if the conditions aren't met or the latch is set. */
function checkOpponentSurprised(state: GameState): GameEvent | null {
  if (state.opponentSurprisedFired) return null;
  // Fires at the start of the opponent's turn when they're in dire straits:
  // empty ring AND HP at or below the threshold fraction of their max.
  const opponentSide = 'opponent';
  if (state.currentPlayer !== opponentSide) return null;
  const ops = getPlayerState(state, opponentSide);
  if (!ringIsEmpty(state, opponentSide)) return null;
  if (ops.hp / state.maxHp > getConfig().surprisedGloatHpThreshold) return null;
  state.opponentSurprisedFired = true;
  return { type: 'opponent-surprised' };
}

function checkOpponentGloat(state: GameState): GameEvent | null {
  if (state.opponentGloatFired) return null;
  // Fires at the end of the opponent's turn when the player's side is in
  // dire straits: empty ring AND HP at or below the threshold fraction.
  const opponentSide = 'opponent';
  if (state.currentPlayer !== opponentSide) return null;
  const ps = getPlayerState(state, 'player');
  if (!ringIsEmpty(state, 'player')) return null;
  if (ps.hp / state.maxHp > getConfig().surprisedGloatHpThreshold) return null;
  state.opponentGloatFired = true;
  return { type: 'opponent-gloat' };
}

const PHASE_ORDER: Phase[] = ['activate', 'draw', 'main', 'attack', 'counter', 'end', 'cleanup'];

// ── Phase machine ────────────────────────────────────────────────
//
// The turn moves through phases via two functions: `advancePhase` (one
// step at a time) and `autoProcessPhase` (does the per-phase work for
// whatever phase the state is currently in). They no longer chain or
// recurse — each call is one logical step. The previous "auto-advance
// through everything until we hit a user-driven phase" was inlined into
// helpers below (`advanceUntilInteractive`), and combat resolution lives
// entirely in CombatResolver.resolveCombat — there is no engine-level
// "resolve combat AND advance to end AND maybe start next turn" anymore.
//
// The split lets callers (UI / AI loops) treat any phase advance the
// same way regardless of how it got there: snapshot → advance → render
// → run banner / draw / stat-deferral pipeline. No more duplicating that
// pipeline once for the no-combat path and once for the post-combat path.

/** Advance one phase forward. Sets the new phase, fires phase-changed,
 *  runs `autoProcessPhase` for the new phase. Crossing the cleanup→
 *  activate boundary cycles via `startNewTurn` (player switch + clock). */
export function advancePhase(
  state: GameState,
  catalog: Map<string, CardDef>,
): { state: GameState; events: GameEvent[] } {
  let next = cloneState(state);
  const allEvents: GameEvent[] = [];

  const currentIndex = PHASE_ORDER.indexOf(next.phase);
  const nextPhase = PHASE_ORDER[(currentIndex + 1) % PHASE_ORDER.length];

  if (nextPhase === 'activate') {
    return startNewTurn(next, catalog);
  }

  next.phase = nextPhase;
  allEvents.push({ type: 'phase-changed', phase: nextPhase, player: next.currentPlayer });

  const autoResult = autoProcessPhase(next, catalog);
  next = autoResult.state;
  allEvents.push(...autoResult.events);

  return { state: next, events: allEvents };
}

/** Run the work the current phase requires once the state has just
 *  entered it (refresh on activate, draw on draw, exhaust + EOT on end,
 *  etc.). Does NOT advance to the next phase; the caller (or the loop
 *  in `advanceUntilInteractive`) does that. */
function autoProcessPhase(
  state: GameState,
  catalog: Map<string, CardDef>,
): { state: GameState; events: GameEvent[] } {
  const events: GameEvent[] = [];

  switch (state.phase) {
    case 'activate': {
      const ps = getPlayerState(state, state.currentPlayer);
      for (const slot of ps.ring) {
        if (!slot.instanceId) continue;
        const inst = getInstance(state, slot.instanceId);
        if (inst.isExhausted) {
          if (inst.skipNextRefresh) {
            // Double-exhaust: skip this refresh, clear the flag
            inst.skipNextRefresh = false;
          } else {
            inst.isExhausted = false;
            events.push({ type: 'character-refreshed', instanceId: slot.instanceId });
          }
        }
        // isFresh is cleared at end of the owner's own turn (see the
        // 'end' case below) — by the time activate fires on the next
        // turn it has already been cleared. Deliberately don't clear
        // here: doing so would mean a card stays "fresh" through the
        // opponent's entire turn.
      }
      ps.ralliesUsed = 0;
      ps.supportsUsed = 0;
      ps.bonusRallies = 0;
      ps.bonusSupports = 0;
      ps.attackLine = [];

      // Snapshot deltas for SearchAI's doom-stall detector. The delta
      // compares "hp/deck NOW (start of this turn cycle)" to
      // "hp/deck at the start of LAST turn cycle". Negative = bleeding
      // at that rate per cycle. Updated BEFORE this turn's draw / EOT
      // triggers run so we only count one full round of passive damage.
      ps.lastTurnHpDelta = ps.hp - ps.lastActivateHp;
      ps.lastTurnDeckDelta = ps.deck.length - ps.lastActivateDeck;
      ps.lastActivateHp = ps.hp;
      ps.lastActivateDeck = ps.deck.length;
      // Reset the per-turn attack flag — it gets set if/when this
      // player dispatches DECLARE_ATTACKERS during the turn ahead.
      ps.attackedDuringTurn = false;

      events.push(...processBeginningOfTurnTriggers(state, state.currentPlayer, catalog));
      sweepPassiveKOs(state, events, catalog);
      const surprised = checkOpponentSurprised(state);
      if (surprised) events.push(surprised);
      return { state, events };
    }

    case 'draw': {
      const ps = getPlayerState(state, state.currentPlayer);
      // Optional legacy compensation: if the rule sets
      // `firstPlayerDrawsOnFirstTurn: false`, the first player skips
      // their first-turn draw to offset first-mover tempo. The current
      // default is `true` (P1 draws like any other turn) — mirror-match
      // testing showed the skip overshot and handed the second player a
      // measurable edge.
      if (state.isFirstTurn && state.currentPlayer === state.firstPlayer && !state.firstPlayerDrawsOnFirstTurn) {
        return { state, events };
      }
      if (ps.deck.length === 0) {
        // Deck out — lose
        const loser = state.currentPlayer;
        const winner = getOpponentSide(loser);
        state.winner = winner;
        state.winReason = 'deckout';
        // Deck-out has no responsible card — the end-game interstitial
        // renders a synthesized "Out of cards" placeholder in scene 1.
        events.push({ type: 'game-over', winner, reason: 'deckout', responsibleCardId: null });
        return { state, events };
      }
      const { drawn, remaining } = drawCards(ps.deck, 1);
      ps.deck = remaining;
      ps.hand.push(...drawn);
      for (const id of drawn) {
        state.cardInstances.get(id)!.zone = 'hand';
        events.push({ type: 'card-drawn', instanceId: id, player: state.currentPlayer });
      }
      return { state, events };
    }

    case 'end': {
      const ps = getPlayerState(state, state.currentPlayer);
      // Exhaust attackers and return them to ring.
      for (const id of ps.attackLine) {
        const inst = state.cardInstances.get(id);
        if (inst && (inst.zone === 'ring' || inst.zone === 'attackLine')) {
          inst.zone = 'ring';
          // passive-attack-ready: this character doesn't exhaust after attacking
          if (!hasPassiveAttackReady(state, id, catalog)) {
            inst.isExhausted = true;
            events.push({ type: 'character-exhausted', instanceId: id });
          }
        }
      }
      ps.attackLine = [];
      state.combatState = null;

      // Wear off Fresh on all of the current player's ring characters.
      // Lets the opponent see at a glance which characters are eligible
      // blockers during their attack — fresh-but-blockable was confusing.
      for (const slot of ps.ring) {
        if (!slot.instanceId) continue;
        const ringInst = getInstance(state, slot.instanceId);
        if (ringInst.isFresh) ringInst.isFresh = false;
      }

      // passive-damage-exhausted: deal damage to exhausted enemies at EOT.
      events.push(...processDamageFrozen(state, state.currentPlayer, catalog));
      // End-of-turn triggers (only for characters still alive in the ring).
      events.push(...processEndOfTurnTriggers(state, state.currentPlayer, catalog));
      sweepPassiveKOs(state, events, catalog);
      const gloat = checkOpponentGloat(state);
      if (gloat) events.push(gloat);
      return { state, events };
    }

    default:
      return { state, events };
  }
}

/** Cross the turn boundary: switch active player, advance the shared
 *  clock if a round just started, set phase to 'activate', and run the
 *  activate-phase work. Stops there — the caller's loop advances further. */
function startNewTurn(
  state: GameState,
  catalog: Map<string, CardDef>,
): { state: GameState; events: GameEvent[] } {
  const events: GameEvent[] = [];

  state.currentPlayer = getOpponentSide(state.currentPlayer);
  if (state.currentPlayer === state.firstPlayer) {
    // Cycled back to whoever went first — a new round starts.
    state.turnNumber++;
    state.isFirstTurn = false;
  } else {
    // Just started the SECOND player's turn (whichever side that is).
    // The shared play-clock advances at the start of every such turn —
    // including their first, which kicks the clock from 0 to 1.
    //   P1 T1: 0 → P2 T1: 1 → P1 T2: 1 → P2 T2: 2 → ...
    // adjustClock wraps maxClock → 1 (never back to 0).
    adjustClock(state, 1);
    events.push({ type: 'clock-changed', clock: state.clock });
  }

  state.phase = 'activate';
  events.push({ type: 'phase-changed', phase: 'activate', player: state.currentPlayer });

  const autoResult = autoProcessPhase(state, catalog);
  events.push(...autoResult.events);

  return { state: autoResult.state, events };
}

/** True if the active player has at least one ring character that could
 *  be staged as an attacker — i.e. not freshly played and not exhausted. */
export function hasEligibleAttackers(state: GameState): boolean {
  const ps = getPlayerState(state, state.currentPlayer);
  return ps.ring.some(s => {
    if (!s.instanceId) return false;
    const inst = getInstance(state, s.instanceId);
    return !inst.isFresh && !inst.isExhausted;
  });
}

/** Whether the given phase requires user / AI input before advancing
 *  further. Used by `advanceUntilInteractive` to know when to stop the
 *  auto-advance loop.
 *
 *  - main: always interactive (player chooses to play, attack, or end).
 *  - attack: interactive only if there's at least one eligible attacker;
 *    otherwise the engine auto-skips through (no decision to make).
 *  - counter: interactive only while combatState is set; combat resolution
 *    nulls it, after which counter auto-advances to end.
 *  - cleanup: interactive only if the player must discard.
 *  - activate / draw / end: never interactive; pure auto-advance phases. */
function shouldAutoAdvance(state: GameState, catalog: Map<string, CardDef>): boolean {
  if (state.winner) return false;
  switch (state.phase) {
    case 'main':
      return false;
    case 'attack':
      return !hasEligibleAttackers(state);
    case 'counter':
      return !state.combatState;
    case 'cleanup': {
      const ps = getPlayerState(state, state.currentPlayer);
      const maxHand = getMaxHandSize(state, state.currentPlayer, catalog);
      return ps.hand.length <= maxHand;
    }
    case 'activate':
    case 'draw':
    case 'end':
      return true;
  }
}

/** Loop `advancePhase` until the state lands in a phase that requires
 *  user / AI input (or the game ends). Returns all events produced
 *  across every advance, so the caller can run the freeze / banner /
 *  draw-stagger pipeline once over the union.
 *
 *  This is the single entry point every dispatch path uses to "let the
 *  turn machine run forward to the next decision point." The pipeline
 *  treats all events uniformly regardless of whether the trigger was
 *  SKIP_PHASE, post-combat, post-discard, or game-start.
 *
 *  `stopAtTurnBoundary` halts the walk when it's about to cross from
 *  the current player's cleanup into the next player's activate. The
 *  caller gets `hitBoundary: true` back and can animate the current
 *  turn's tail (end-of-turn triggers, KOs, HP hits) fully before
 *  making a second call to walk the opponent's setup phases. Without
 *  this split the state advances all the way to opponent-main in one
 *  pass, the renderer paints opponent-main, and only then do the
 *  end-of-turn effect banners play out on top — which reads as
 *  "end-of-turn effects happen on my opponent's turn." */
export function advanceUntilInteractive(
  state: GameState,
  catalog: Map<string, CardDef>,
  options?: { stopAtTurnBoundary?: boolean },
): { state: GameState; events: GameEvent[]; hitBoundary: boolean } {
  let next = state;
  const events: GameEvent[] = [];
  let hitBoundary = false;
  while (shouldAutoAdvance(next, catalog)) {
    // A cleanup that shouldAutoAdvance = true means the hand is fine
    // and the very next advancePhase call will invoke startNewTurn
    // (cleanup → activate flips currentPlayer). Bail out here so the
    // caller can render/animate the current-turn tail before we
    // touch the next-turn state.
    if (options?.stopAtTurnBoundary && next.phase === 'cleanup') {
      hitBoundary = true;
      break;
    }
    const result = advancePhase(next, catalog);
    next = result.state;
    events.push(...result.events);
  }
  return { state: next, events, hitBoundary };
}

/** Skip directly to the counter phase (entered when an attacker declares).
 *  Doesn't go through `advancePhase` because counter is normally only
 *  reachable via combat declaration, not by phase progression — and the
 *  defender's `passive-cannot-block` banners are tied to counter entry. */
export function skipToCounterPhase(
  state: GameState,
  catalog: Map<string, CardDef>,
): { state: GameState; events: GameEvent[] } {
  const next = cloneState(state);
  next.phase = 'counter';
  const events: GameEvent[] = [
    { type: 'phase-changed', phase: 'counter', player: next.currentPlayer },
  ];

  // passive-cannot-block: emit one banner per defender's non-exhausted
  // ring character with this passive — they would otherwise be eligible
  // blockers and the player needs to know the passive is the reason they
  // can't. Exhausted characters can't block regardless, so skip them.
  const defenderSide = getOpponentSide(next.currentPlayer);
  const defenderState = getPlayerState(next, defenderSide);
  for (const slot of defenderState.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(next, slot.instanceId);
    if (inst.isExhausted) continue;
    if (hasPassive(next, slot.instanceId, 'passive-cannot-block', catalog)) {
      events.push({ type: 'ability-triggered', instanceId: slot.instanceId, abilityType: 'passive', effect: 'passive-cannot-block' });
    }
  }

  return { state: next, events };
}
